import React, { useMemo, useState, memo, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { readLayerPanelUi, writeLayerPanelUi } from '../../Utils/layerPanelUi';
import { fmtGrade } from '../../Utils/gradePalette';
import {
  isBlockModelLayer,
  useBlockModelFilterStore,
  DEFAULT_BLOCK_MODEL_MIN,
  resolvedGradeMin,
} from '../../store/blockModelFilterStore';
import { useClickStore } from '../../store/clickStore';
import LoadHolesForm from './LoadHolesForm';
import HoleLayerActions from './HoleLayerActions';
import EditableGradeLegend, { GradeBoundInput, LithLegendBody } from '../ui/EditableGradeLegend';

const CATALOGUE_TABS = [
  { id: 'browse', label: 'Browse', title: 'Folder tree of all layers' },
  { id: 'visible', label: 'Visible', title: 'Layers currently on in the scene' },
  { id: 'cached', label: 'Cached', title: 'Layers stored on this device' },
  { id: 'recent', label: 'Recent', title: 'Recently used layers' },
];

const TOOL_TABS = [
  { id: 'holes', label: 'Holes', title: 'Query holes and edit the hole legend' },
  { id: 'blockmodels', label: 'BlockModels', title: 'Block model grade filter and colour legend' },
];

const getDisplayName = (model) => {
  const raw = model?.displayName || model?.label || model?.name || '';
  if (!raw) return '';

  if (model?.type === 'databaseHoles' && model?.dataset && !model.queryLayer && !model.sharedQuery) {
    const ds = String(model.dataset);
    const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(^|_)${escapeRegExp(ds)}(_|$)`, 'ig');
    return String(raw)
      .replace(re, '$1')
      .replace(/__+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  return String(raw);
};

const groupDatabaseHolesByDataset = (models) => {
  const byDataset = new Map();
  const other = [];

  (models || []).forEach((m) => {
    if (m?.type === 'databaseHoles' && m?.dataset) {
      const ds = String(m.dataset);
      if (!byDataset.has(ds)) byDataset.set(ds, []);
      byDataset.get(ds).push(m);
    } else {
      other.push(m);
    }
  });

  const datasets = Array.from(byDataset.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dataset, modelsInDataset]) => ({
      dataset,
      models: modelsInDataset.sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b))),
    }));

  return { datasets, other: other.sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b))) };
};

/** Group flat modelList entries by their `group` field (e.g. Drone, Holes, Plan). */
const nestSectionsByCategory = (list) => {
  const map = new Map();
  (list || []).forEach((section) => {
    const cat = section?.group != null && String(section.group).trim() !== ''
      ? String(section.group)
      : 'Other';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat).push(section);
  });
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, sections]) => ({
      category,
      sections: sections.sort((x, y) =>
        String(x.name || '').localeCompare(String(y.name || '')),
      ),
    }));
};

/** Folder path keys used by Browse expand/collapse for a catalogue layer. */
export function browsePathsForLayer(modelList, layerKey) {
  if (!layerKey) return [];
  const categories = nestSectionsByCategory(modelList);
  for (const cat of categories) {
    for (const section of cat.sections) {
      const model = (section.models || []).find((m) => m.name === layerKey);
      if (!model) continue;
      const paths = [`cat:${cat.category}`];
      const singleSection = cat.sections.length === 1;
      const pathPrefix = singleSection ? cat.category : `${cat.category}::${section.name || 'Unnamed'}`;
      if (!singleSection) {
        paths.push(`sec:${cat.category}::${section.name || 'Unnamed'}`);
      }
      if (model.type === 'databaseHoles' && model.dataset && !model.queryLayer && !model.sharedQuery) {
        paths.push(`ds:${pathPrefix}::${model.dataset}`);
      }
      return paths;
    }
  }
  return [];
}

const modelMatchesSearch = (model, searchText) => {
  const q = String(searchText || '').toLowerCase();
  if (!q) return true;
  return (
    String(model.name || '').toLowerCase().includes(q) ||
    getDisplayName(model).toLowerCase().includes(q) ||
    String(model.dataset || '').toLowerCase().includes(q)
  );
};

const Chevron = ({ expanded, className = '' }) => (
  <span
    className={`inline-flex w-4 h-4 shrink-0 items-center justify-center text-yellow-600/70 ${className}`}
    aria-hidden
  >
    <svg
      viewBox="0 0 16 16"
      className={`w-3 h-3 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
      fill="currentColor"
    >
      <path d="M6 4l4 4-4 4V4z" />
    </svg>
  </span>
);

const FolderGlyph = ({ open }) => (
  <span className="inline-flex w-4 h-4 shrink-0 items-center justify-center" aria-hidden>
    {open ? (
      <svg viewBox="0 0 16 16" className="w-4 h-4 text-yellow-500">
        <path
          fill="currentColor"
          d="M2 4a2 2 0 012-2h3l1 1h6a2 2 0 012 2v1H2V4zm0 2v7a2 2 0 002 2h10a2 2 0 002-2V6H2z"
        />
      </svg>
    ) : (
      <svg viewBox="0 0 16 16" className="w-4 h-4 text-yellow-600/80">
        <path
          fill="currentColor"
          d="M2 4a2 2 0 012-2h3l1 1h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V4z"
        />
      </svg>
    )}
  </span>
);

const FileGlyph = () => (
  <span className="inline-flex w-4 h-4 shrink-0 items-center justify-center text-zinc-500" aria-hidden>
    <svg viewBox="0 0 16 16" className="w-3.5 h-3.5">
      <path
        fill="currentColor"
        d="M4 2h5l3 3v9a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1zm4 1v3h3L8 3z"
      />
    </svg>
  </span>
);

const TreeLines = ({ children }) => (
  <div className="border-l border-yellow-600/20 pl-1.5 ml-1.5">{children}</div>
);

/** idle | loading | cached | visible — failed loads map to idle (not loaded). */
export const getLayerLoadStatus = (layerVisibility, modelName) => {
  const s = layerVisibility[modelName];
  if (!s) return 'idle';
  const loadStatus = s.loadStatus ?? 'idle';
  const visible = !!s.visible;
  if (loadStatus === 'loading') return 'loading';
  if (loadStatus === 'failed') return 'idle';
  if (loadStatus === 'ready' && visible) return 'visible';
  if (loadStatus === 'ready' && !visible) return 'cached';
  if (s.diskCached && !visible) return 'cached';
  return 'idle';
};

const LayerStatusDot = ({ status }) => {
  const meta = {
    idle: { label: 'Not loaded yet', className: 'bg-red-500/80' },
    loading: { label: 'Loading…', className: 'border-2 border-yellow-400 border-t-transparent animate-spin' },
    cached: { label: 'Cached on this device', className: 'bg-zinc-500' },
    visible: { label: 'Loaded and visible', className: 'bg-emerald-500' },
  }[status] || { label: '', className: 'bg-zinc-600' };

  return (
    <span
      className={`inline-block shrink-0 w-2 h-2 rounded-full ${meta.className} ${
        status === 'loading' ? '' : 'border border-black/40'
      }`}
      title={meta.label}
      role="img"
      aria-label={meta.label}
    />
  );
};

const FlatLayerList = ({
  models,
  emptyMessage,
  layerVisibility,
  onToggleVisibility,
  onIsolate,
  onOpacityChange,
  isolatedKeys,
  onReveal,
}) => {
  if (!models.length) {
    return <p className="text-[11px] text-zinc-500 px-2 py-4 text-center">{emptyMessage}</p>;
  }
  return models.map((model) => (
    <LeafLayerRow
      key={model.name}
      model={model}
      layerVisibility={layerVisibility}
      onToggleVisibility={onToggleVisibility}
      onIsolate={onIsolate}
      onOpacityChange={onOpacityChange}
      isolated={(isolatedKeys || []).includes(model.name)}
      onReveal={onReveal}
    />
  ));
};

const BlockModelsTab = ({ models, emptyMessage, layerVisibility, onReveal }) => {
  const filter = useBlockModelFilterStore((s) => s.filter);
  const layerMeta = useBlockModelFilterStore((s) => s.layerMeta);
  const setFilter = useBlockModelFilterStore((s) => s.setFilter);
  const resetFilter = useBlockModelFilterStore((s) => s.resetFilter);
  const legendOpen = useBlockModelFilterStore((s) => s.legendOpen);
  const toggleLegend = useBlockModelFilterStore((s) => s.toggleLegend);
  const lithLegends = useClickStore((s) => s.lithLegends);

  const columns = useMemo(() => {
    const seen = new Set();
    const list = [];
    models.forEach((model) => {
      (layerMeta[model.name]?.columns || []).forEach((col) => {
        if (!col || seen.has(col)) return;
        seen.add(col);
        list.push(col);
      });
    });
    return list;
  }, [models, layerMeta]);

  const dataRange = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    models.forEach((model) => {
      const meta = layerMeta[model.name];
      if (Number.isFinite(meta?.dataMin) && meta.dataMin < min) min = meta.dataMin;
      if (Number.isFinite(meta?.dataMax) && meta.dataMax > max) max = meta.dataMax;
    });
    return {
      min: Number.isFinite(min) ? min : null,
      max: Number.isFinite(max) ? max : null,
    };
  }, [models, layerMeta]);

  const min = resolvedGradeMin(filter);
  const max = Number.isFinite(filter.max) ? filter.max : dataRange.max;
  const colorBy = filter.colorBy || '';
  const isCustom = Number.isFinite(filter.min) || Number.isFinite(filter.max) || !!filter.colorBy;
  const loaded = columns.length > 0;
  const lithCodes = useMemo(() => {
    const names = new Set(models.map((m) => m.name));
    const codes = [];
    const seen = new Set();
    Object.keys(lithLegends || {}).forEach((name) => {
      if (!names.has(name)) return;
      (lithLegends[name] || []).forEach((code) => {
        const key = String(code || '').trim();
        if (!key || seen.has(key)) return;
        seen.add(key);
        codes.push(key);
      });
    });
    return codes;
  }, [models, lithLegends]);

  return (
    <div className="space-y-1.5 px-0.5">
      <EditableGradeLegend overlayOpen={legendOpen} onToggleOverlay={toggleLegend}>
        <p className="text-[9px] text-zinc-500 leading-snug">
          Applies to every block model. Min defaults to {fmtGrade(DEFAULT_BLOCK_MODEL_MIN)} g/t so waste is not drawn.
        </p>
        <label className="block">
          <span className="block text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">Colour by</span>
          <select
            value={colorBy}
            disabled={!loaded}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setFilter({ colorBy: e.target.value || null })}
            className="w-full min-w-0 px-1.5 py-1 text-[11px] border border-yellow-600/25 rounded-sm bg-black/60 text-zinc-200 focus:outline-none focus:border-yellow-500/50 disabled:opacity-50"
            aria-label="Block models colour by"
          >
            <option value="">{loaded ? 'Grade (default)' : 'Turn a block model on to load columns'}</option>
            {columns.map((col) => (
              <option key={col} value={col}>
                {col}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-1.5">
          <label className="block">
            <span className="block text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">
              Min{dataRange.min != null ? ` (data ${fmtGrade(dataRange.min)})` : ''}
            </span>
            <GradeBoundInput
              value={min}
              placeholder={fmtGrade(DEFAULT_BLOCK_MODEL_MIN)}
              ariaLabel="Block models min grade"
              onCommit={(n) => setFilter({ min: n })}
            />
          </label>
          <label className="block">
            <span className="block text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">
              Max{dataRange.max != null ? ` (${fmtGrade(dataRange.max)})` : ''}
            </span>
            <GradeBoundInput
              value={max}
              placeholder={dataRange.max != null ? fmtGrade(dataRange.max) : 'data max'}
              ariaLabel="Block models max grade"
              onCommit={(n) => setFilter({ max: n })}
            />
          </label>
        </div>
        {isCustom && (
          <button
            type="button"
            title={`Reset to ${fmtGrade(DEFAULT_BLOCK_MODEL_MIN)} g/t min and data max`}
            onClick={resetFilter}
            className="m-0 px-1 text-[9px] uppercase tracking-wider text-yellow-600/80 hover:text-yellow-400"
          >
            Reset filter
          </button>
        )}
      </EditableGradeLegend>
      {lithCodes.length > 0 && (
        <div className="rounded-sm border border-yellow-600/20 bg-black/30 px-1.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Lithology</p>
          <LithLegendBody codes={lithCodes} hideable={false} />
        </div>
      )}
      {!models.length ? (
        <p className="text-[11px] text-zinc-500 px-2 py-4 text-center">{emptyMessage}</p>
      ) : (
        <div className="space-y-0.5">
          <p className="text-[9px] uppercase tracking-wider text-zinc-500 px-1">Layers</p>
          {models.map((model) => {
            const status = getLayerLoadStatus(layerVisibility, model.name);
            return (
              <div key={model.name} className="flex items-center gap-1 px-1 py-0.5">
                <LayerStatusDot status={status} />
                <span className="truncate flex-1 text-[11px] text-zinc-200">{getDisplayName(model)}</span>
                {typeof onReveal === 'function' && (
                  <button
                    type="button"
                    title="Show in Browse"
                    onClick={() => onReveal(model.name)}
                    className="shrink-0 px-1 text-[9px] uppercase tracking-wider text-zinc-500 hover:text-yellow-400"
                  >
                    Show
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const LayerControl = ({
  modelList,
  layerVisibility,
  onToggleVisibility,
  onIsolate,
  onOpacityChange,
  isolatedKeys = [],
  onRefresh,
  refreshing,
  projectName,
  recentLayerKeys = [],
}) => {
  const bootUi = useMemo(() => readLayerPanelUi(projectName), [projectName]);
  const [searchText, setSearchText] = useState(bootUi.searchText || '');
  const [activeTab, setActiveTab] = useState(bootUi.activeTab || 'browse');
  const [expandedPaths, setExpandedPaths] = useState(() => new Set(bootUi.expandedPaths || []));
  const [highlightKey, setHighlightKey] = useState(null);

  const scrollRef = useRef(null);
  const scrollByTabRef = useRef({ ...(bootUi.scrollByTab || {}) });
  const scrollSaveTimerRef = useRef(null);
  const uiPersistTimerRef = useRef(null);

  const persistUi = useCallback(
    (patch) => {
      if (scrollSaveTimerRef.current) {
        clearTimeout(scrollSaveTimerRef.current);
        scrollSaveTimerRef.current = null;
      }
      if (uiPersistTimerRef.current) clearTimeout(uiPersistTimerRef.current);
      uiPersistTimerRef.current = setTimeout(() => {
        writeLayerPanelUi(projectName, {
          activeTab,
          expandedPaths: Array.from(expandedPaths),
          scrollByTab: scrollByTabRef.current,
          searchText,
          ...patch,
        });
      }, 200);
    },
    [projectName, activeTab, expandedPaths, searchText]
  );

  const saveScrollForTab = useCallback(
    (tabId) => {
      const el = scrollRef.current;
      if (!el || !tabId) return;
      scrollByTabRef.current = { ...scrollByTabRef.current, [tabId]: el.scrollTop };
      persistUi({ scrollByTab: scrollByTabRef.current });
    },
    [persistUi]
  );

  const handleTabChange = useCallback(
    (tabId) => {
      saveScrollForTab(activeTab);
      setActiveTab(tabId);
      persistUi({ activeTab: tabId });
    },
    [activeTab, persistUi, saveScrollForTab]
  );

  const handleToggleExpand = useCallback(
    (pathKey) => {
      setExpandedPaths((prev) => {
        const next = new Set(prev);
        if (next.has(pathKey)) next.delete(pathKey);
        else next.add(pathKey);
        writeLayerPanelUi(projectName, {
          expandedPaths: Array.from(next),
          activeTab,
          scrollByTab: scrollByTabRef.current,
          searchText,
        });
        return next;
      });
    },
    [projectName, activeTab, searchText]
  );

  const handleSearchChange = useCallback(
    (value) => {
      setSearchText(value);
      persistUi({ searchText: value });
    },
    [persistUi]
  );

  const handleReveal = useCallback(
    (layerKey) => {
      if (!layerKey) return;
      const paths = browsePathsForLayer(modelList, layerKey);
      setSearchText('');
      setExpandedPaths((prev) => {
        const next = new Set(prev);
        paths.forEach((p) => next.add(p));
        writeLayerPanelUi(projectName, {
          expandedPaths: Array.from(next),
          activeTab: 'browse',
          scrollByTab: scrollByTabRef.current,
          searchText: '',
        });
        return next;
      });
      setHighlightKey(layerKey);
      setActiveTab('browse');
      persistUi({ activeTab: 'browse', searchText: '' });
    },
    [modelList, projectName, persistUi]
  );

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (highlightKey && activeTab === 'browse') return;
    el.scrollTop = scrollByTabRef.current[activeTab] || 0;
  }, [activeTab, highlightKey]);

  useLayoutEffect(() => {
    if (!highlightKey || activeTab !== 'browse') return undefined;
    const id = window.setTimeout(() => {
      const root = scrollRef.current;
      if (!root) return;
      const node = root.querySelector(`[data-layer-key="${typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(highlightKey) : highlightKey}"]`);
      node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 80);
    const clear = window.setTimeout(() => setHighlightKey(null), 3500);
    return () => {
      window.clearTimeout(id);
      window.clearTimeout(clear);
    };
  }, [highlightKey, activeTab, expandedPaths]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      if (scrollSaveTimerRef.current) clearTimeout(scrollSaveTimerRef.current);
      scrollSaveTimerRef.current = setTimeout(() => {
        scrollByTabRef.current = { ...scrollByTabRef.current, [activeTab]: el.scrollTop };
        writeLayerPanelUi(projectName, {
          activeTab,
          expandedPaths: Array.from(expandedPaths),
          scrollByTab: scrollByTabRef.current,
          searchText,
        });
      }, 150);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (scrollSaveTimerRef.current) clearTimeout(scrollSaveTimerRef.current);
    };
  }, [activeTab, projectName, expandedPaths, searchText]);

  useEffect(
    () => () => {
      saveScrollForTab(activeTab);
      if (uiPersistTimerRef.current) clearTimeout(uiPersistTimerRef.current);
    },
    [activeTab, saveScrollForTab]
  );

  const allModels = useMemo(() => {
    const models = [];
    (modelList || []).forEach((section) => {
      (section.models || []).forEach((model) => models.push(model));
    });
    return models;
  }, [modelList]);

  const filteredFlat = useMemo(() => {
    return (modelList || []).map((section) => ({
      ...section,
      models: (section.models || []).filter((model) => modelMatchesSearch(model, searchText)),
    })).filter((section) => (section.models || []).length > 0);
  }, [modelList, searchText]);

  const categories = useMemo(() => nestSectionsByCategory(filteredFlat), [filteredFlat]);

  const visibleModels = useMemo(
    () =>
      allModels.filter(
        (m) => layerVisibility[m.name]?.visible && modelMatchesSearch(m, searchText)
      ),
    [allModels, layerVisibility, searchText]
  );

  const cachedModels = useMemo(
    () =>
      allModels.filter(
        (m) =>
          getLayerLoadStatus(layerVisibility, m.name) === 'cached' &&
          modelMatchesSearch(m, searchText)
      ),
    [allModels, layerVisibility, searchText]
  );

  const recentModels = useMemo(
    () =>
      recentLayerKeys
        .map((key) => allModels.find((m) => m.name === key))
        .filter((m) => m && modelMatchesSearch(m, searchText)),
    [recentLayerKeys, allModels, searchText]
  );

  const blockModels = useMemo(
    () => allModels.filter((m) => isBlockModelLayer(m) && modelMatchesSearch(m, searchText)),
    [allModels, searchText]
  );

  const tabCounts = useMemo(
    () => ({
      browse: filteredFlat.reduce((n, s) => n + (s.models?.length || 0), 0),
      holes: allModels.filter((m) => m.queryLayer && !m.sharedQuery).length,
      visible: visibleModels.length,
      cached: cachedModels.length,
      recent: recentModels.length,
      blockmodels: blockModels.length,
    }),
    [filteredFlat, allModels, visibleModels.length, cachedModels.length, recentModels.length, blockModels.length]
  );

  const rowProps = {
    layerVisibility,
    onToggleVisibility,
    onIsolate,
    onOpacityChange,
    isolatedKeys,
    highlightKey,
  };

  return (
    <div className="w-full h-full flex flex-col bg-transparent">
      <div className="px-2.5 py-2 flex-shrink-0 border-b border-yellow-600/20">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-yellow-400 text-[11px] font-semibold uppercase tracking-[0.14em] leading-tight">
              Layers
            </h2>
          </div>
          {typeof onRefresh === 'function' && (
            <button
              type="button"
              onClick={() => onRefresh()}
              disabled={!!refreshing}
              className="shrink-0 px-2 py-1 text-[10px] font-semibold rounded border border-yellow-600/40 text-yellow-400 hover:bg-yellow-500/10 disabled:opacity-50"
              title="Rescan models folder on the server"
            >
              {refreshing ? '…' : 'Refresh'}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-x-2.5 gap-y-1 mt-2 text-[9px] text-zinc-500 uppercase tracking-wider">
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500/80 shrink-0" aria-hidden /> Idle
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full border border-yellow-400 border-t-transparent animate-spin shrink-0" aria-hidden /> Load
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 shrink-0" aria-hidden /> Cached
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" aria-hidden /> On
          </span>
        </div>
      </div>

      <div className="shrink-0 border-b border-yellow-600/15 bg-black/30 px-1 py-1 space-y-1">
        <div
          className="flex w-full min-w-0 overflow-hidden rounded-sm border border-yellow-600/20"
          role="tablist"
          aria-label="Layer lists"
        >
          {CATALOGUE_TABS.map((tab, i) => {
            const count = tabCounts[tab.id];
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                title={tab.title}
                onClick={() => handleTabChange(tab.id)}
                className={`m-0 flex-1 min-w-0 px-0.5 py-1.5 text-[9px] font-semibold uppercase tracking-wide leading-none transition-colors ${
                  i > 0 ? 'border-l border-yellow-600/20' : ''
                } ${
                  selected
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-yellow-500/5'
                }`}
              >
                <span className="block truncate">
                  {tab.label}
                  {count != null && (
                    <span className={`ml-0.5 font-normal tabular-nums ${selected ? 'text-yellow-600/90' : 'text-zinc-600'}`}>
                      {count}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-0.5" role="tablist" aria-label="Layer tools">
          {TOOL_TABS.map((tab) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                title={tab.title}
                onClick={() => handleTabChange(tab.id)}
                className={`m-0 min-w-0 px-1.5 py-1.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider leading-none transition-colors ${
                  selected
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-600/35'
                    : 'text-zinc-400 hover:text-zinc-200 border border-yellow-600/15 hover:border-yellow-600/30'
                }`}
              >
                <span className="block truncate">
                  {tab.label}
                  <span className={`ml-1 font-normal tabular-nums ${selected ? 'text-yellow-600/90' : 'text-zinc-600'}`}>
                    {tabCounts[tab.id]}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-1 py-1.5 sm:px-1.5">
        {activeTab === 'holes' ? (
          <LoadHolesForm
            layerVisibility={layerVisibility}
            onToggleVisibility={onToggleVisibility}
            searchText={searchText}
          />
        ) : refreshing && !(modelList || []).length ? (
          <div className="text-[11px] text-zinc-500 px-3 py-6 text-center space-y-2">
            <span className="inline-block h-4 w-4 rounded-full border-2 border-yellow-500/40 border-t-yellow-400 animate-spin" />
            <p className="font-medium text-zinc-300">Loading layers…</p>
            <p className="text-[10px] text-zinc-600">Scanning models folder on the server</p>
          </div>
        ) : !(modelList || []).length ? (
          <div className="text-[11px] text-zinc-500 px-3 py-4 text-center space-y-2">
            <p className="font-medium text-zinc-300">No layers loaded</p>
            <p>
              Check <code className="bg-zinc-900 px-1 rounded text-yellow-600/80">/server/list_models.php</code> and{' '}
              <code className="bg-zinc-900 px-1 rounded text-yellow-600/80">MODELS_ROOT</code>.
            </p>
          </div>
        ) : activeTab === 'browse' ? (
          categories.length === 0 ? (
            <p className="text-[11px] text-zinc-500 px-2 py-4 text-center">No layers match your search.</p>
          ) : (
            categories.map((cat) => (
              <CategoryFolder
                key={cat.category}
                category={cat.category}
                sections={cat.sections}
                expandedPaths={expandedPaths}
                onToggleExpand={handleToggleExpand}
                {...rowProps}
              />
            ))
          )
        ) : activeTab === 'visible' ? (
          <FlatLayerList
            models={visibleModels}
            emptyMessage={searchText ? 'No visible layers match your search.' : 'No visible layers.'}
            onReveal={handleReveal}
            {...rowProps}
          />
        ) : activeTab === 'cached' ? (
          <FlatLayerList
            models={cachedModels}
            emptyMessage={searchText ? 'No cached layers match your search.' : 'No cached layers — load an object once and it stays on this device.'}
            onReveal={handleReveal}
            {...rowProps}
          />
        ) : activeTab === 'recent' ? (
          <FlatLayerList
            models={recentModels}
            emptyMessage={searchText ? 'No recent layers match your search.' : 'No recent layers yet — toggle or edit a layer to add it here.'}
            onReveal={handleReveal}
            {...rowProps}
          />
        ) : (
          <BlockModelsTab
            models={blockModels}
            emptyMessage={
              searchText
                ? 'No block models match your search.'
                : 'No block models yet — drop a .zip into models/<Mine>/BlockModels/ and Refresh.'
            }
            layerVisibility={layerVisibility}
            onReveal={handleReveal}
          />
        )}
      </div>

      <div className="p-2 border-t border-yellow-600/20 flex-shrink-0">
        <div className="flex items-center">
          <input
            type="text"
            placeholder="Search…"
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="flex-1 px-2 py-1.5 text-xs border border-yellow-600/25 rounded-l bg-black/50 text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-yellow-500/50"
          />
          <button
            type="button"
            onClick={() => handleSearchChange('')}
            className="text-zinc-500 hover:text-yellow-400 w-8 py-1.5 flex items-center justify-center border border-yellow-600/25 border-l-0 rounded-r bg-black/50"
            title="Clear search"
          >
            <span className="text-xs">✕</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const CategoryFolder = ({
  category,
  sections,
  expandedPaths,
  onToggleExpand,
  layerVisibility,
  onToggleVisibility,
  onIsolate,
  onOpacityChange,
  isolatedKeys,
  highlightKey,
}) => {
  const pathKey = `cat:${category}`;
  const open = expandedPaths.has(pathKey);
  const singleSection = sections.length === 1;

  return (
    <div className="mb-1 select-none">
      <button
        type="button"
        onClick={() => onToggleExpand(pathKey)}
        className="w-full flex items-center gap-0.5 py-1.5 px-1.5 rounded-sm text-left text-xs font-medium text-zinc-200 hover:bg-yellow-500/10 border border-transparent hover:border-yellow-600/25"
      >
        <Chevron expanded={open} />
        <FolderGlyph open={open} />
        <span className="truncate flex-1">{category}</span>
        <span className="text-[10px] text-zinc-600 shrink-0 tabular-nums">
          {sections.reduce((n, s) => n + (s.models?.length || 0), 0)}
        </span>
      </button>

      {open && (
        <TreeLines>
          {singleSection ? (
            <SectionContent
              section={sections[0]}
              expandedPaths={expandedPaths}
              onToggleExpand={onToggleExpand}
              layerVisibility={layerVisibility}
              onToggleVisibility={onToggleVisibility}
              onIsolate={onIsolate}
              onOpacityChange={onOpacityChange}
              isolatedKeys={isolatedKeys}
              highlightKey={highlightKey}
              pathPrefix={category}
            />
          ) : (
            sections.map((section) => (
              <SectionFolder
                key={`${category}::${section.name}`}
                section={section}
                expandedPaths={expandedPaths}
                onToggleExpand={onToggleExpand}
                layerVisibility={layerVisibility}
                onToggleVisibility={onToggleVisibility}
                onIsolate={onIsolate}
                onOpacityChange={onOpacityChange}
                isolatedKeys={isolatedKeys}
                highlightKey={highlightKey}
                pathPrefix={category}
              />
            ))
          )}
        </TreeLines>
      )}
    </div>
  );
};

const SectionFolder = ({
  section,
  expandedPaths,
  onToggleExpand,
  layerVisibility,
  onToggleVisibility,
  onIsolate,
  onOpacityChange,
  isolatedKeys,
  highlightKey,
  pathPrefix,
}) => {
  const label = section.name || 'Unnamed';
  const pathKey = `sec:${pathPrefix}::${label}`;
  const open = expandedPaths.has(pathKey);

  return (
    <div className="mb-0.5">
      <button
        type="button"
        onClick={() => onToggleExpand(pathKey)}
        className="w-full flex items-center gap-0.5 py-1 px-1 rounded-sm text-left text-[11px] font-medium text-zinc-300 hover:bg-yellow-500/10 border border-transparent hover:border-yellow-600/20"
      >
        <Chevron expanded={open} />
        <FolderGlyph open={open} />
        <span className="truncate flex-1">{label}</span>
        <span className="text-[10px] text-zinc-600 shrink-0 tabular-nums">
          {(section.models || []).length}
        </span>
      </button>
      {open && (
        <TreeLines>
          <SectionContent
            section={section}
            expandedPaths={expandedPaths}
            onToggleExpand={onToggleExpand}
            layerVisibility={layerVisibility}
            onToggleVisibility={onToggleVisibility}
            onIsolate={onIsolate}
            onOpacityChange={onOpacityChange}
            isolatedKeys={isolatedKeys}
            highlightKey={highlightKey}
            pathPrefix={`${pathPrefix}::${label}`}
          />
        </TreeLines>
      )}
    </div>
  );
};

const SectionContent = ({
  section,
  expandedPaths,
  onToggleExpand,
  layerVisibility,
  onToggleVisibility,
  onIsolate,
  onOpacityChange,
  isolatedKeys,
  highlightKey,
  pathPrefix,
}) => {
  const namedQueryFolder = section.name === 'Loaded queries' || section.name === 'Shared queries';
  if (namedQueryFolder) {
    return (section.models || []).map((model) => (
      <LeafLayerRow
        key={model.name}
        model={model}
        layerVisibility={layerVisibility}
        onToggleVisibility={onToggleVisibility}
        onIsolate={onIsolate}
        onOpacityChange={onOpacityChange}
        isolated={(isolatedKeys || []).includes(model.name)}
        highlighted={highlightKey === model.name}
      />
    ));
  }

  const { datasets, other } = groupDatabaseHolesByDataset(section.models);

  return (
    <>
      {datasets.map(({ dataset, models }) => (
        <DatasetFolder
          key={`${pathPrefix}::${dataset}`}
          dataset={dataset}
          models={models}
          pathPrefix={pathPrefix}
          expandedPaths={expandedPaths}
          onToggleExpand={onToggleExpand}
          layerVisibility={layerVisibility}
          onToggleVisibility={onToggleVisibility}
          onIsolate={onIsolate}
          onOpacityChange={onOpacityChange}
          isolatedKeys={isolatedKeys}
          highlightKey={highlightKey}
        />
      ))}
      {other.map((model) => (
        <LeafLayerRow
          key={model.name}
          model={model}
          layerVisibility={layerVisibility}
          onToggleVisibility={onToggleVisibility}
          onIsolate={onIsolate}
          onOpacityChange={onOpacityChange}
          isolated={(isolatedKeys || []).includes(model.name)}
          highlighted={highlightKey === model.name}
        />
      ))}
    </>
  );
};

const DatasetFolder = ({
  dataset,
  models,
  pathPrefix,
  expandedPaths,
  onToggleExpand,
  layerVisibility,
  onToggleVisibility,
  onIsolate,
  onOpacityChange,
  isolatedKeys,
  highlightKey,
}) => {
  const pathKey = `ds:${pathPrefix}::${dataset}`;
  const open = expandedPaths.has(pathKey);

  return (
    <div className="mb-0.5">
      <button
        type="button"
        onClick={() => onToggleExpand(pathKey)}
        className="w-full flex items-center gap-0.5 py-1 px-1 rounded-sm text-left text-[11px] text-zinc-300 hover:bg-yellow-500/10 border border-transparent hover:border-yellow-600/20"
      >
        <Chevron expanded={open} />
        <FolderGlyph open={open} />
        <span className="truncate flex-1 font-medium">{dataset}</span>
        <span className="text-[10px] text-zinc-600 shrink-0 tabular-nums">{models.length}</span>
      </button>
      {open && (
        <TreeLines>
          {models.map((model) => (
            <LeafLayerRow
              key={model.name}
              model={model}
              layerVisibility={layerVisibility}
              onToggleVisibility={onToggleVisibility}
              onIsolate={onIsolate}
              onOpacityChange={onOpacityChange}
              isolated={(isolatedKeys || []).includes(model.name)}
              highlighted={highlightKey === model.name}
            />
          ))}
        </TreeLines>
      )}
    </div>
  );
};

const LeafLayerRow = memo(function LeafLayerRow({
  model,
  layerVisibility,
  onToggleVisibility,
  onIsolate,
  onOpacityChange,
  isolated,
  onReveal,
  highlighted,
}) {
  const status = getLayerLoadStatus(layerVisibility, model.name);
  const visible = !!(layerVisibility[model.name]?.visible);
  const opacity = layerVisibility[model.name]?.opacity ?? 1;
  const label = getDisplayName(model);
  const onToggle = useCallback(() => onToggleVisibility(model.name), [onToggleVisibility, model.name]);
  const isGlb = !model.type || String(model.type).toLowerCase() === 'glb';

  return (
    <div
      data-layer-key={model.name}
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      className={`flex items-center gap-1 py-0.5 px-1 rounded-sm hover:bg-yellow-500/10 cursor-pointer text-[11px] text-zinc-300 ${
        isolated || highlighted ? 'bg-yellow-500/15 ring-1 ring-yellow-500/40' : ''
      }`}
    >
      <LayerStatusDot status={status} />
      <input
        type="checkbox"
        checked={visible}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        className="w-3 h-3 shrink-0 text-yellow-500 border-zinc-600 rounded bg-black focus:ring-yellow-500 focus:ring-1"
      />
      <FileGlyph />
      <span className="truncate flex-1">{label}</span>
      <HoleLayerActions model={model} />
      {typeof onReveal === 'function' && (
        <button
          type="button"
          title="Reveal in Browse folders"
          className="m-0 h-5 px-1 rounded-sm text-[8px] uppercase tracking-wider shrink-0 border border-yellow-600/30 text-yellow-500/80 hover:bg-yellow-500/10"
          onClick={(e) => {
            e.stopPropagation();
            onReveal(model.name);
          }}
        >
          Reveal
        </button>
      )}
      {typeof onIsolate === 'function' && (
        <button
          type="button"
          title={isolated ? 'Remove from isolate — last one restores the previous view' : 'Isolate this layer — click Iso on more layers to keep several'}
          className={`m-0 h-5 px-1 rounded-sm text-[8px] uppercase tracking-wider shrink-0 ${
            isolated
              ? 'bg-yellow-500 text-black'
              : 'border border-yellow-600/30 text-yellow-500/80 hover:bg-yellow-500/10'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onIsolate(model.name);
          }}
        >
          Iso
        </button>
      )}
      {isGlb && typeof onOpacityChange === 'function' && visible && (
        <input
          type="range"
          min="0.15"
          max="1"
          step="0.05"
          value={opacity}
          title={`Opacity ${Math.round(opacity * 100)}%`}
          className="w-12 h-3 shrink-0 accent-yellow-500"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            onOpacityChange(model.name, Number(e.target.value));
          }}
        />
      )}
    </div>
  );
}, (prev, next) => {
  const a = prev.layerVisibility[prev.model.name];
  const b = next.layerVisibility[next.model.name];
  return (
    prev.model.name === next.model.name &&
    prev.onToggleVisibility === next.onToggleVisibility &&
    prev.onIsolate === next.onIsolate &&
    prev.onOpacityChange === next.onOpacityChange &&
    prev.onReveal === next.onReveal &&
    prev.isolated === next.isolated &&
    prev.highlighted === next.highlighted &&
    a?.visible === b?.visible &&
    (a?.opacity ?? 1) === (b?.opacity ?? 1) &&
    (a?.loadStatus ?? 'idle') === (b?.loadStatus ?? 'idle')
  );
});

export default LayerControl;
